// @flow

import * as React from 'react';

import { UserRound } from 'lucide-react';

import { connect } from 'react-redux';

import { selectCartUUID, CategoryBadge } from '@gandi/cart';
import { CountryName, SubdivisionName } from '@gandi/react-places';
import type { Organization } from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';
import { Badge, Base, Button } from '@gandi/ui';

import { selectShopCurrent } from '../../modules/organization/selectors';
import type { PreFetchArgs, FullState, PreFetchReturn } from '../../types';

import styles from './InvoiceTo.scss';

type AddressProps = {
  org: Organization,
};

function Address({ org }: AddressProps): React.Node {
  return (
    <address>
      {org.firstname} {org.lastname}, {org.streetaddr} {org.streetaddr2},{' '}
      {org.zip} {org.city}{' '}
      {org.state && (
        <SubdivisionName country={org.country} subdivision={org.state} />
      )}{' '}
      <CountryName country={org.country || ''} />
    </address>
  );
}

type StateProps = {
  org: ?Organization,
  configurationLink: string,
};

type Props = {
  ...StateProps,
};

function InvoiceTo({ org, configurationLink }: Props): null | React.Node {
  const { __ } = useTranslator();

  // User is not logged-in. The invoice recipient hasn't been set yet.
  if (!org) {
    return null;
  }

  const orgname = org.type !== 'individual' ? org.orgname : null;
  const fullname =
    org.firstname && org.lastname ? `${org.firstname} ${org.lastname}` : null;
  return (
    <Base backgroundColor="surface" flex flexCol className={styles.root}>
      <CategoryBadge>{__('Invoice to')}</CategoryBadge>

      <div>
        <div className={styles.iconBlock}>
          <UserRound />
        </div>
        <Badge className={styles.badge} intent="muted">
          {org.type_label}
        </Badge>
        <div className={styles.name}>{orgname || fullname}</div>
      </div>

      <Address org={org} />

      <Button kind="link" to={configurationLink}>
        {__('Change')}
      </Button>
    </Base>
  );
}

InvoiceTo.preFetch = function preFetch(
  args: PreFetchArgs<{}, void, empty, FullState>,
): PreFetchReturn<> {
  const { dispatch, getState } = args;
  const org = selectShopCurrent(args.getState(), true);
  const country = org?.country;

  const promises = [CountryName.preFetch({ dispatch, getState })];

  if (country) {
    promises.push(
      SubdivisionName.preFetch({
        dispatch,
        getState,
        country,
        // FIXME: subdivision is useless in SubdivisionName
        subdivision: '',
      }),
    );
  }

  return Promise.allSettled(promises);
};

const mapStateToProps = (state: FullState): Partial<Props> => {
  const cartId = selectCartUUID(state);
  const configurationLink = `/cart/${cartId}/configure/account-choice`;
  const org = selectShopCurrent(state, true);

  return {
    org,
    configurationLink,
  };
};

// $FlowFixMe[signature-verification-failure]
// $FlowFixMe[underconstrained-implicit-instantiation]
export default connect(mapStateToProps)(InvoiceTo);
